/**
 * Canvas clipboard (Zustand): the items Cmd/Ctrl+C or Cmd/Ctrl+X put aside on
 * the workspace canvas, for a Cmd/Ctrl+V in the open collection. An APP
 * clipboard, never the system one: it holds the rows (kind, parent,
 * `canEdit`), which the copy and the bulk move need.
 *
 * Scoped to the workspace it was filled in (`clipboardFor` hands no other
 * workspace anything), and `clearWorkspaceQueries` (the workspace switch and
 * sign-out path) empties it. It survives moving between collections: that is
 * the point. Not persisted.
 */

import { create } from 'zustand';
import type { WorkspaceItem } from '@bower/shared';

export type ClipboardMode = 'copy' | 'cut';

export interface CanvasClipboard {
  readonly mode: ClipboardMode;
  readonly items: readonly WorkspaceItem[];
}

interface CanvasClipboardState {
  workspaceId: string | null;
  clip: CanvasClipboard | null;
  put: (workspaceId: string, mode: ClipboardMode, items: readonly WorkspaceItem[]) => void;
  /** Empties a pending CUT (Escape); a copy stays. True when it did. */
  cancelCut: () => boolean;
  clear: () => void;
}

export const useCanvasClipboardStore = create<CanvasClipboardState>()((set, get) => ({
  workspaceId: null,
  clip: null,
  put: (workspaceId, mode, items) => set({ workspaceId, clip: { mode, items } }),
  cancelCut: () => {
    if (get().clip?.mode !== 'cut') return false;
    set({ workspaceId: null, clip: null });
    return true;
  },
  clear: () => set({ workspaceId: null, clip: null }),
}));

type State = Pick<CanvasClipboardState, 'workspaceId' | 'clip'>;

/** What `workspaceId` may paste, or null. */
export function clipboardFor(state: State, workspaceId: string): CanvasClipboard | null {
  return state.workspaceId === workspaceId ? state.clip : null;
}

/** Is `id` cut and waiting for a paste? */
export function isCutIn(state: State, id: string): boolean {
  return state.clip?.mode === 'cut' && state.clip.items.some((item) => item.id === id);
}

/** Draws a cut item dimmed until it is pasted or the cut is cancelled. */
export function useIsCut(id: string): boolean {
  return useCanvasClipboardStore((state) => isCutIn(state, id));
}
